import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import CountUp from 'react-countup';
import './WhyJoinSection.css';

const WhyJoinSection: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const fadeIn = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8 } }
  };

  const statsContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const statItem = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const stats = [
    {
      end: 50000,
      suffix: '+',
      description: 'Students trained across our various programs and workshops nationwide',
      duration: 2.5
    },
    {
      end: 500,
      suffix: '+',
      description: 'Workshops conducted, delivering hands-on STEM learning experiences',
      duration: 2
    },
    {
      end: 100,
      suffix: '+',
      description: 'Schools across 10+ states, making quality tech education accessible',
      duration: 1.5
    },
    {
      text: 'IIIT Delhi',
      description: 'Proudly supported by one of India\'s top tech institutes'
    }
  ];

  const features = [
    {
      icon: '👨‍🏫',
      title: 'Expert Instructors',
      description: 'Our instructors are industry professionals and educators with extensive experience in their fields. They bring real-world knowledge and passion to the classroom, ensuring your child receives the highest quality education.'
    },
    {
      icon: '🛠️',
      title: 'Hands-on Learning',
      description: 'We believe in learning by doing. Our curriculum is designed to maximize hands-on experience, allowing students to build, program, and test their creations in a supportive environment that encourages experimentation and problem-solving.'
    },
    {
      icon: '👥',
      title: 'Small Class Sizes',
      description: 'With a maximum of 20 students per class and a 1:10 instructor-to-student ratio, we ensure that every child receives personalized attention and guidance throughout the camp, maximizing their learning potential.'
    },
    {
      icon: '🏆',
      title: 'Take-Home Projects',
      description: 'Students don\'t just learn at camp—they create projects they can take home and continue to develop. These tangible results of their hard work serve as both a source of pride and a platform for ongoing learning and exploration.'
    }
  ];

  return (
    <section className="section why-join-section" id="why-choose">
      <div className="container">
        <motion.div
          className="section-header"
          ref={ref}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          variants={fadeIn}
        >
          <h2 className="section-title text-center" style={{
            background: 'linear-gradient(135deg, #6366f1, #10b981)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            display: 'inline-block'
          }}>
            Why Choose Dream Advance?
          </h2>
          <p className="section-subtitle text-center" style={{ color: '#000000' }}>
            Join thousands of satisfied parents and students who have experienced our world-class STEM education programs.
          </p>
        </motion.div>

        <motion.div
          className="stats-container"
          variants={statsContainer}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {stats.map((stat, index) => (
            <motion.div 
              key={index} 
              className="stat-item"
              variants={statItem}
            >
              <div className="stat-number">
                {stat.text ? (
                  stat.text
                ) : (
                  <CountUp
                    end={stat.end!}
                    suffix={stat.suffix}
                    duration={stat.duration}
                    separator=","
                    delay={index * 0.2}
                    start={inView ? 0 : undefined}
                  />
                )}
              </div>
              <div className="stat-description">{stat.description}</div>
            </motion.div>
          ))}
        </motion.div>

        <div className="features-container">
          {features.map((feature, index) => (
            <motion.div 
              key={index} 
              className="feature-card"
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ delay: 0.3 + (index * 0.1), duration: 0.6 }}
            >
              <div className="feature-icon">{feature.icon}</div>
              <h3 className="feature-title">{feature.title}</h3>
              <p className="feature-description">{feature.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyJoinSection;
